import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms'
import { Observable } from 'rxjs'
import { validateEmail } from '../../shared/validators/email.validator'
import { AuthService } from '../../singleton-services/auth/auth.service'
import type { IUserIdentity } from '../../singleton-services/auth/auth.interfaces'
import { LoggingService } from 'src/app/singleton-services/logging/logging.service'
import { DuplicateAsyncValidators } from '../../shared/validators/duplicate-async.validators'
import { take, map } from 'rxjs/operators'

@Component({
  selector: 'flo-email-login',
  templateUrl: './email-login.component.html',
  styleUrls: ['./email-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailLoginComponent implements OnInit {
  @Output()
  readonly emailLoginSuccess = new EventEmitter<IUserIdentity | undefined>()
  public emailLoginSubmitting = false
  public showPassword = false
  public loginForm = this.fb.group({
    email: ['', [Validators.required, validateEmail], [this.duplicateAsyncValidators.validateUnclaimedAccountExists()]],
    password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(75)]]
  })
  public showEmailSpinner$: Observable<boolean>
  public togglePassword(event: Event) {
    event.stopPropagation()
    this.showPassword = !this.showPassword
  }
  public get email() {
    return this.loginForm.get('email') as UntypedFormControl
  }
  public get password() {
    return this.loginForm.get('password') as UntypedFormControl
  }
  public showFormErrorComponent() {
    return (
      this.loginForm.controls.email.touched &&
      this.loginForm.controls.email.invalid &&
      !this.loginForm.controls.email.hasError('unclaimed_account')
    )
  }

  private get emailValue() {
    return this.email && this.email.value
  }
  private get passwordValue() {
    return this.password && this.password.value
  }
  public get emailControl(): UntypedFormControl {
    return this.loginForm.controls.email as UntypedFormControl
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly auth: AuthService,
    private readonly logger: LoggingService,
    private readonly changeRef: ChangeDetectorRef,
    private readonly duplicateAsyncValidators: DuplicateAsyncValidators
  ) {}

  ngOnInit() {
    if (this.email) {
      this.showEmailSpinner$ = this.email.statusChanges.pipe(map(status => status === 'PENDING'))
    }
  }

  public get canSubmitForm() {
    return this.loginForm.valid && !this.loginForm.pending && !this.emailLoginSubmitting
  }

  public loginByEmail() {
    this.markAllAsTouched()
    if (!this.loginForm.valid || this.emailLoginSubmitting) return
    this.emailLoginSubmitting = true

    this.auth
      .authorizeViaHttp(this.auth.generateToken(this.emailValue, this.passwordValue))
      .pipe(take(1))
      .subscribe(
        user => {
          this.emailLoginSubmitting = false
          this.emailLoginSuccess.next(user)
        },
        err => {
          this.emailLoginSubmitting = false
          this.logger.error('Error authorizing token: ', err.message, err.error)
          if (this.password) {
            this.password.setErrors({ server_error: true })
          }
          this.changeRef.markForCheck()
        }
      )
  }

  private markAllAsTouched() {
    Object.keys(this.loginForm.controls).forEach(item => {
      this.loginForm.controls[item].markAsTouched()
    })
  }
}
